import { ProfileUpdateReminderTooltip } from '@/shared/components/ProfileUpdateReminderTooltip'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Typography } from '@/shared/components/ui/typography'
import { useUsers } from '@/shared/hooks/data-hooks/users/useUsers'
import { useFeatureFlagsWithWorkspace } from '@/shared/hooks/useFeatureFlags'
import { useSidebar } from '@/shared/hooks/useSidebar'
import { cn } from '@/shared/lib/utils'
import type { CollaboratorWithLastViewed } from '@/types/collaborator'
import type { PlaybookUserAction } from '@/types/playbook'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import DownloadOutlined from '@mui/icons-material/DownloadOutlined'
import MenuOutlined from '@mui/icons-material/MenuOutlined'
import ShareOutlined from '@mui/icons-material/ShareOutlined'
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined'
import React, { ComponentProps } from 'react'
import { usePlaybookViewContext } from '../contexts'
import { usePlaybookQueryContext } from '../hooks/usePlaybookQueryContext'
import { CollaboratorAvatars } from './CollaboratorAvatars'
import { PlaybookActionMenu } from './PlaybookActionMenu'
import { ShareMenu } from './ShareMenu'
import { VersionDropdown } from './VersionDropdown'
import { VisibilityDropdown } from './VisibilityDropdown'

type PlaybookHeaderProps = ComponentProps<'div'> & {
  onPlaybookAction?: (action: PlaybookUserAction, payload?: unknown) => void
}

export const PlaybookHeader: React.FC<PlaybookHeaderProps> = ({
  onPlaybookAction,
  ...props
}) => {
  const {
    data: { data: playbook } = {},
    isLoading: isLoadingPlaybook,
    title,
  } = usePlaybookQueryContext()

  const { flags: [
    isSharingEnabled,
    areNotesEnabled,
    isDownloadEnabled,
    isCollaborationEnabled,
  ] } = useFeatureFlagsWithWorkspace([
    'playbookSharing',
    'playbookNotes',
    'playbookDownload',
    'playbookCollaboration',
  ])

  const { isShown: isShowingPlaybook, toggle: togglePlaybookView } =
    usePlaybookViewContext()
  const { toggleOpen: toggleSidebarOpen } = useSidebar()

  const collaboratorIds = playbook?.collaborators
    ? Object.keys(playbook.collaborators)
    : []
  const { users: collaborators = [] } = useUsers({
    userIds: collaboratorIds,
    enabled: collaboratorIds.length > 0,
  })

  // TODO: use real data
  const collaboratorsWithLastViewed: CollaboratorWithLastViewed[] =
    collaborators.map((user, index) => {
      const mockLastViewedTimes = [
        new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
        new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
        new Date(Date.now() - 8 * 24 * 60 * 60 * 1000), // 8 days ago
      ]

      return {
        ...user,
        lastViewed:
          mockLastViewedTimes[index] ||
          new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // Default to 1 day ago
      }
    })

  const handleActionMenuItemSelection = async (
    item: 'duplicate' | 'versionHistory' | 'delete' | 'copyLink'
  ) => {
    onPlaybookAction?.(item)
  }

  return (
    <div
      {...props}
      className={cn(
        'PlaybookHeader relative flex w-full bg-transparent px-3 py-2 md:px-6 md:py-2',
        props.className
      )}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-1 items-center gap-1 overflow-hidden md:max-w-[50%] md:gap-2">
          <div className="md:hidden">
            {isShowingPlaybook ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={togglePlaybookView}
                className=""
              >
                <CloseOutlined sx={{ fontSize: 20 }} />
              </Button>
            ) : (
              <ProfileUpdateReminderTooltip>
                <Button
                  size="sm"
                  onClick={toggleSidebarOpen}
                  variant="ghost"
                  className=""
                >
                  <MenuOutlined sx={{ fontSize: 24 }} />
                </Button>
              </ProfileUpdateReminderTooltip>
            )}
          </div>
          {isLoadingPlaybook ? (
            <Skeleton className="h-4 w-96 bg-grey-03 md:h-6" />
          ) : (
            <Typography
              className="truncate text-sm font-semibold md:text-base"
              title={title}
            >
              {title}
            </Typography>
          )}

          <VisibilityDropdown
            isLoading={isLoadingPlaybook}
            isPublic={playbook?.is_public}
            playbook={playbook}
            onRename={(data) => {
              onPlaybookAction?.('rename', { playbook, newTitle: data.title })
            }}
            onPlaybookAction={(action, payload) =>
              onPlaybookAction?.(action, payload)
            }
          />

          <VersionDropdown
            isLoading={isLoadingPlaybook}
            currentVersion={playbook?.current_version || 'Initial version'}
            totalVersions={playbook?.total_versions || 1}
            triggerProps={{
              className: 'hidden md:flex',
            }}
          />
        </div>

        <div
          className={cn(
            'items-center gap-2',
            !isShowingPlaybook && 'hidden md:flex'
          )}
        >
          {isCollaborationEnabled && (
            <CollaboratorAvatars collaborators={collaboratorsWithLastViewed} />
          )}

          {isSharingEnabled && (
            <ShareMenu
              users={collaborators.map((user) => ({
                id: user._id || '',
                name: `${user.firstName} ${user.lastName}`,
                email: user.email,
                permission:
                  playbook?.collaborators?.[user._id || ''] === 'edit'
                    ? 'can edit'
                    : 'can view',
              }))}
              onInvite={(_emails) => {
                // TODO: Implement invite functionality
              }}
              onCopyLink={() => {
                // TODO: Implement copy link functionality
              }}
              onPermissionChange={(_userId, _permission) => {
                // TODO: Implement permission change functionality
              }}
              onAccessLevelChange={(_level) => {
                // TODO: Implement access level change functionality
              }}
              onRemoveUser={(_userId) => {
                // TODO: Implement remove user functionality
              }}
            >
              <Button
                variant="ghost"
                size="sm"
                className="cursor-pointer rounded-full border bg-grey-03/10"
              >
                <ShareOutlined sx={{ fontSize: 16 }} className="mr-2" />
                Share
              </Button>
            </ShareMenu>
          )}

          {areNotesEnabled && (
            <Button
              variant="ghost"
              size="icon"
              className="cursor-pointer bg-grey-03/10"
            >
              <StickyNote2Outlined sx={{ fontSize: 16 }} />
            </Button>
          )}

          {isDownloadEnabled && (
            <Button
              variant="ghost"
              size="icon"
              className="cursor-pointer bg-grey-03/10"
            >
              <DownloadOutlined sx={{ fontSize: 16 }} />
            </Button>
          )}

          <PlaybookActionMenu
            playbook={playbook}
            onPlaybookAction={handleActionMenuItemSelection}
          />
        </div>
      </div>
    </div>
  )
}
